import React, { useState, useEffect } from 'react';
import { X, Music, Upload, CheckCircle, AlertCircle } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { editLyrics, getLyrics, getOrderById, uploadLyrics } from '../../redux/reduxSlices/ordersSlice';
import toast from 'react-hot-toast';

const LyricsUploadPopup = ({
    uploadLyricsPopup,
    setUploadLyricsPopup,
    orderId,
    isEditingLyrics,
    setIsEditingLyrics,
    lyricsForEditing,
    setLyricsForEditing,
    onAfterSubmit,
}) => {

    const [lyrics, setLyrics] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const dispatch = useDispatch();

    useEffect(() => {
        if (isEditingLyrics) {
            setLyrics(lyricsForEditing?.text || "");
        }
    }, [isEditingLyrics, lyricsForEditing]);

    useEffect(() => {
        if (!uploadLyricsPopup) {
            setLyrics('');
            setError('');
            setSubmitting(false);
            setSuccess(false);
        }
    }, [uploadLyricsPopup]);

    const handleSubmit = async () => {
        if (!lyrics.trim()) {
            setError('Please enter lyrics before submitting');
            return;
        }

        setError('');
        setSubmitting(true);

        try {
            if (!isEditingLyrics) {
                const payload = {
                    "text": lyrics.trim()
                };

                const response = await dispatch(uploadLyrics({ payload, orderId })).unwrap();

                if (response.success) {
                    setSuccess(true);
                    await dispatch(getOrderById(orderId))

                    toast.success('Lyrics uploaded successfully!', {
                        duration: 3000,
                        position: 'top-right',
                    });

                    setTimeout(() => {
                        handleClosePopup();
                    }, 2000);

                } else {
                    throw new Error(response?.error?.message || 'Failed to upload lyrics');
                }
            } else if (isEditingLyrics) {
                const payload = {
                    "order_id": orderId,
                    "lyric_id": lyricsForEditing.lyric_id,
                    "text": lyrics.trim()
                }
                const response = await dispatch(editLyrics(payload)).unwrap();
                if (response.success) {
                    setSuccess(true);
                    if (onAfterSubmit) {
                        await onAfterSubmit();
                    }
                    toast.success('Lyrics updated successfully!', {
                        duration: 3000,
                        position: 'top-right',
                    });

                    setTimeout(() => {
                        handleClosePopup();
                    }, 2000);

                } else {
                    throw new Error(response?.error?.message || 'Failed to update lyrics');
                }
            }
        } catch (error) {
            console.error('Upload error:', error);
            setError(error.message || 'Failed to submit lyrics. Please try again.');

            toast.error('Failed to upload lyrics', {
                duration: 4000,
                position: 'top-right',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleClosePopup = () => {
        setLyrics("");
        setError("");
        setSubmitting(false);
        setSuccess(false);
        setIsEditingLyrics(false);
        setLyricsForEditing(null);
        setUploadLyricsPopup(false);
    };

    const handleCancel = () => {
        if (submitting) return;
        handleClosePopup();
    };

    if (!uploadLyricsPopup) return null;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/40 backdrop-blur-sm bg-opacity-50 transition-opacity"
                onClick={handleCancel}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    className="relative bg-white rounded-lg shadow-lg w-full max-w-lg transform transition-all"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <div className="flex items-center gap-2">
                            <div className="p-1.5 bg-orange-100 rounded">
                                {success ? (
                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                ) : (
                                    <Music className="w-4 h-4 text-orange-600" />
                                )}
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-gray-900">
                                    {success ? (isEditingLyrics ? "Edit Successful!" : 'Upload Successful!') : isEditingLyrics ? "Edit Lyrics" : 'Upload Lyrics'}
                                </h3>
                                <p className="text-xs text-gray-500">
                                    {success
                                        ? (isEditingLyrics ? "Lyrics updated successfully" : 'Lyrics uploaded successfully')
                                        : 'Enter your lyrics below'
                                    }
                                </p>
                            </div>
                        </div>
                        {!success && (
                            <button
                                onClick={handleCancel}
                                disabled={submitting}
                                className="p-1 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                            >
                                <X className="w-4 h-4 text-gray-500" />
                            </button>
                        )}
                    </div>

                    {/* Content */}
                    <div className="p-4">
                        {success ? (
                            // Success State
                            <div className="flex flex-col items-center justify-center py-6">
                                <div className="mb-4">
                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                        <CheckCircle className="w-8 h-8 text-green-600" />
                                    </div>
                                </div>
                                <h4 className="text-base font-semibold text-gray-900 mb-1.5">
                                    {isEditingLyrics ? "Lyrics Updated" : "Lyrics Uploaded!"}
                                </h4>
                                <p className="text-gray-600 text-center text-sm mb-4">
                                    Lyrics submitted successfully.
                                </p>
                                <div className="text-xs text-gray-500">
                                    Closing automatically...
                                </div>
                            </div>
                        ) : (
                            // Form State
                            <div className="space-y-3">
                                {error && (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded">
                                        <div className="flex items-center gap-1.5 text-red-700 text-sm">
                                            <AlertCircle className="w-4 h-4" />
                                            <span>{error}</span>
                                        </div>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                        Lyrics
                                        <span className="text-red-500 ml-1">*</span>
                                    </label>
                                    <textarea
                                        className="w-full p-3 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 transition-colors resize-none bg-white hover:border-gray-400 min-h-[150px] disabled:bg-gray-50 disabled:cursor-not-allowed"
                                        rows={8}
                                        value={lyrics}
                                        onChange={(e) => {
                                            setLyrics(e.target.value);
                                            if (error) setError('');
                                        }}
                                        placeholder="🎵 Write your creative lyrics here...

Example:
TechFlow, TechFlow
Making life better, helping you grow
Innovation in every beat
Making your work life sweet!"
                                        disabled={submitting}
                                    />
                                    <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500">
                                        <Upload className="w-3 h-3" />
                                        <span>Paste or type your lyrics</span>
                                    </div>
                                </div>

                                {/* Character count */}
                                <div className="flex justify-between items-center text-xs">
                                    <span className={`${lyrics.length < 10 ? 'text-red-500' : 'text-gray-500'}`}>
                                        {lyrics.length} characters
                                        {lyrics.length < 10 && ' (min 10)'}
                                    </span>
                                    <span className="text-gray-500">
                                        {lyrics.trim().split(/\s+/).length} words
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    {!success && (
                        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                            <button
                                onClick={handleCancel}
                                disabled={submitting}
                                className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>

                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleSubmit}
                                    disabled={submitting || !lyrics.trim() || lyrics.trim().length < 10}
                                    className="bg-gradient-to-br from-orange-500 to-orange-600 text-white px-4 py-2 rounded text-sm font-semibold hover:shadow transition-shadow disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-1.5"
                                >
                                    {submitting ? (
                                        <>
                                            <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <Music className="w-3.5 h-3.5" />
                                            Submit Lyrics
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LyricsUploadPopup;